import {
  OtpSend,
  OtpStatus,
  Profile,
  Session,
  TokenStatus,
  Tokens,
  UsernameAvailability,
  VerifyToken,
} from "@/api/auth/auth.types.ts";
import { authAxios, baseAxios } from "@/api/axios.ts";

class AuthRequests {
  static async postCheckUsername(username: string) {
    const formData = new FormData();
    formData.append("username", username);
    return baseAxios.post<UsernameAvailability>("/user/check-username", formData);
  }

  static async postSendCode(username: string) {
    const formData = new FormData();
    formData.append("username", username);
    return baseAxios.post<OtpSend>("/user/send-code", formData).then((res) => res.data);
  }

  static async googleLogin(formData: FormData) {
    return baseAxios
      .post("/user/google-auth", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => res.data);
  }

  static async postCheckCode(username: string, code: string) {
    const formData = new FormData();
    formData.append("username", username);
    formData.append("code", code);
    return baseAxios.post("/user/check-code", formData).then((res) => res.data);
  }

  static async postCheckCodeGetToken(username: string, code: string) {
    const formData = new FormData();
    formData.append("username", username);
    formData.append("code", code);
    return baseAxios.post<TokenStatus>("/user/check-code-get-token", formData).then((res) => res.data);
  }

  static async postSetPassword(code: string, password: string) {
    const formData = new FormData();
    formData.append("code", code);
    formData.append("password", password);
    const token = localStorage.getItem("splay:token") as string;
    return baseAxios
      .post<TokenStatus>("/user/set-password", formData, {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => res.data);
  }

  static async getSessions(token: string) {
    return baseAxios
      .get<Session[]>("/user/sessions", {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => res.data);
  }

  static async postUsernameAvailability(username: string) {
    const formData = new FormData();
    formData.append("username", username);
    return authAxios.post<UsernameAvailability>(`/user/username-availability`, formData).then((res) => res.data);
  }

  static async postLoginPassword(data: { username: string; password: string }) {
    const formData = new FormData();
    formData.append("username", data.username);
    formData.append("password", data.password);
    return authAxios
      .post<TokenStatus>("/user/login", formData)
      .then((res) => res.data)
      .catch((error) => error);
  }

  static async postSessionDelete(data: { token: string; key: string }) {
    const formData = new FormData();
    formData.append("my_key", data.key);
    return baseAxios
      .post(`/user/sessions`, formData, { headers: { Authorization: `Token ${data.token}` } })
      .then((res) => res.data);
  }

  static async postResetOtp(username: string) {
    const formData = new FormData();
    formData.append("username", username);
    return authAxios.post<OtpStatus>(`/user/reset-password-send-code`, formData).then((res) => res.data);
  }

  static async postCheckResetOtp(data: { account_id: number; code: string }) {
    const formData = new FormData();

    formData.append("account_id", data.account_id.toString());
    formData.append("code", data.code);

    return authAxios.post<OtpStatus>(`/user/check-verification-code`, formData).then((res) => res.data);
  }

  static async postResetPassword(data: { code: string; password: string; account_id: number }) {
    const formData = new FormData();
    formData.append("code", data.code);
    formData.append("password", data.password);
    formData.append("account_id", data.account_id.toString());
    return authAxios.post<TokenStatus>(`/user/reset-password`, formData).then((res) => res.data);
  }

  static async getSelectProfileList() {
    const token = localStorage.getItem("splay:token");
    return authAxios
      .get<{ data: Profile[] }>(`/user/select-profile`, { headers: { Authorization: `Token ${token}` } })
      .then((res) => res.data);
  }

  static async postCreateJwtToken(data: { profile_id: string; pin_code?: string }): Promise<Tokens> {
    const token = localStorage.getItem("splay:token");
    const formData = new FormData();
    formData.append("profile_id", data.profile_id);
    if (data.pin_code) {
      formData.append("pin_code", data.pin_code);
    }

    return baseAxios
      .post<{ data: Tokens }>("/user/token", formData, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((res) => res.data.data);
  }

  static async postVerifyAccessToken(access: string) {
    const formData = new FormData();
    formData.append("token", access);
    return baseAxios.post<VerifyToken>("/user/token/verify", formData).then((res) => res.data);
  }

  static async postRefreshAccessToken(refresh: string) {
    const formData = new FormData();
    formData.append("refresh", refresh);
    return baseAxios.post<Pick<Tokens, "access">>("/user/token/refresh", formData).then((res) => res.data);
  }
}

export default AuthRequests;
